import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/format";
import { Lock, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const firstLogin = params.get("first_login") === "1";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      // Clear session so the user must sign in with the new password
      try { await api.post("/auth/logout"); } catch {}
      localStorage.removeItem("modav_token");
      setDone(true);
      toast.success("Password updated. Please sign in with your new password.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9F8] p-6">
        <div className="card-modav p-8 max-w-md w-full text-center">
          <h2 className="font-serif text-2xl font-bold text-[#1A2E24]">Invalid reset link</h2>
          <p className="text-[#5E7166] mt-2 text-sm">The link you used is missing a token. Please request a new password reset email.</p>
          <Link to="/forgot-password" className="btn-gold mt-4 mx-auto">Request New Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F9F8] p-6">
      <div className="card-modav p-8 w-full max-w-md" data-testid="reset-password-card">
        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F5EE] text-[#0E5C3A] flex items-center justify-center mb-4"><CheckCircle2 size={28}/></div>
            <h2 className="font-serif text-2xl font-bold">Password updated</h2>
            <p className="text-[#5E7166] mt-2 text-sm">Redirecting to sign-in…</p>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-2xl font-bold text-[#1A2E24]">{firstLogin ? "Set your new password" : "Set a new password"}</h2>
            <p className="text-sm text-[#5E7166] mt-1">{firstLogin ? "Welcome! For your security, please replace the temporary password we emailed you with a new one (8+ characters)." : "Choose a strong password (8+ characters) for your Modav Homes account."}</p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">New password</span>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E7166]"/>
                  <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="input-modav pl-10" autoComplete="new-password" data-testid="new-password"/>
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Confirm password</span>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E7166]"/>
                  <input type="password" required value={confirm} onChange={e=>setConfirm(e.target.value)} className="input-modav pl-10" autoComplete="new-password" data-testid="confirm-password"/>
                </div>
              </label>
              <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="reset-submit">{submitting ? "Saving…" : "Update password"}</button>
            </form>
          </>
        )}
        <Link to="/login" className="mt-4 inline-flex items-center gap-1 text-sm text-[#0E5C3A]"><ArrowLeft size={14}/> Back to sign-in</Link>
      </div>
    </div>
  );
}
